import {Button, CircularProgress, Grid, Tooltip} from '@material-ui/core';
import {ButtonProps} from '@material-ui/core/Button';
import {Info} from '@material-ui/icons';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncError, ServerError} from 'Core/models/Async';
import React, {ReactNode, useState} from 'react';
import {useDispatch} from 'react-redux';
import 'Shared/components/FileDownloadButton.sass';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

type FileDownloadButtonProps = {
  downloadedFileName: string;
  extension?: FileExtension;
  relativeFileLocation: string;
  children: ReactNode;
  className?: string;
  displayTooltip: boolean;
  tooltipMessage?: string;
} & ButtonProps;

export enum FileExtension {
  XLSX = '.xlsx',
  TIFF = '.tiff',
  PDF = '.pdf',
  CSV = '.csv',
  XLS = '.xls',
}

const FileDownloadButton = (props: FileDownloadButtonProps) => {
  const {
    downloadedFileName,
    extension = '',
    relativeFileLocation,
    className,
    children,
    displayTooltip,
    tooltipMessage,
    ...buttonProps
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const defaultTooltipMessage =
    (displayTooltip && !tooltipMessage) || (extension === FileExtension.CSV && !tooltipMessage)
      ? 'Only the first 5,000 records will be extracted'
      : tooltipMessage!;

  return (
    <Grid container item alignItems="center">
      {isLoading ? (
        <Grid item>
          <CircularProgress className="file-download-button__loading" />
        </Grid>
      ) : (
        <>
          {displayTooltip && (
            <Tooltip title={defaultTooltipMessage} aria-label="info">
              <Info color="primary" />
            </Tooltip>
          )}
          <Grid item>
            <Tooltip title={defaultTooltipMessage} aria-label="info">
              <Button
                {...buttonProps}
                id={`file-download-button-${toKebabCase(downloadedFileName)}`}
                className={className}
                color="primary"
                onClick={() => {
                  downloadFile(relativeFileLocation, `${downloadedFileName}${extension}`)
                    .then((response: AsyncError | string) => {
                      if (typeof response === 'string') {
                        dispatch(createSnackbar(response));
                      } else {
                        if (response instanceof ServerError) {
                          dispatch(createSnackbar('An Error Occurred While Downloading the Enrollment File.'));
                        } else {
                          dispatch(createSnackbar(response.message));
                        }
                      }
                    })
                    .then(() => {
                      setIsLoading(false);
                    });

                  setIsLoading(true);
                }}
              >
                {children}
              </Button>
            </Tooltip>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default FileDownloadButton;
